import { FC, useEffect, useState } from "react";
import "./ModalChatToolQuestions.scss";
import { Button, Card, Col, FormControl, FormGroup, InputGroup, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import SkeletonTable from "../../../../components/SkeletonTable/SkeletonTable";
import { ChatToolQuestionsService } from "../../../../services/Chats/ChatToolQuestionsService";
import { ChatToolModel } from "../../../../models/Chat/ChatTool/ChatToolModel";
import { ChatToolQuestionModel } from "../../../../models/Chat/ChatTool/ChatToolQuestionModel";

interface ModalChatToolQuestionsProps {
  show: boolean;
  onClose: () => void;
  tool: ChatToolModel | null;
}

const ModalChatToolQuestions: FC<ModalChatToolQuestionsProps> = ({
  show,
  onClose,
  tool,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [questions, setQuestions] = useState<ChatToolQuestionModel[]>([]);
  const [questionInput, setQuestionInput] = useState<string>("");

  const service = new ChatToolQuestionsService();

  const loadQuestions = async () => {
    if (!tool?.id) return;
    setIsLoading(true);
    const [response, error] = await service.list({
      filter: { chat_tool_id: tool.id, only_active: true },
      withPaginate: false,
    });
    setIsLoading(false);
    if (error) {
      toast.error(response?.message || error);
      return;
    }
    setQuestions(response?.items ?? []);
  };

  useEffect(() => {
    if (show && tool?.id) {
      setQuestionInput("");
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tool?.id]);

  const handleAdd = async () => {
    const text = questionInput.trim();
    if (!text) {
      toast.warning("Digite uma pergunta.");
      return;
    }

    setIsSaving(true);
    const [response, error] = await service.fetch({
      chat_tool_id: tool?.id,
      text,
    });
    setIsSaving(false);

    if (error) {
      toast.error(response?.message || error);
      return;
    }

    toast.success("Pergunta adicionada com sucesso!");
    setQuestionInput("");
    loadQuestions();
  };

  const handleDelete = (item: ChatToolQuestionModel) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja remover esta pergunta?</span>`,
      icon: "question",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const [response, error] = await service.delete(item.id || 0);
        if (error) {
          toast.error(response?.message || error);
          return;
        }
        toast.success("Pergunta removida com sucesso!");
        loadQuestions();
      }
    });
  };

  const renderTable = () => {
    if (isLoading) return <SkeletonTable />;

    return (
      <div className="table-responsive">
        <table className="table table-flush m-0 w-100">
          <thead className="thead-light">
            <tr>
              <th className="text-uppercase opacity-7" scope="col">
                Pergunta
              </th>
              <th className="text-uppercase opacity-7 text-center" scope="col">
                Ação
              </th>
            </tr>
          </thead>
          <tbody>
            {questions?.length > 0 ? (
              questions.map((x: ChatToolQuestionModel) => (
                <tr key={x.id} className="text-sm">
                  <td className="text-wrap">{x.text}</td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="cursor-pointer"
                      onClick={() => handleDelete(x)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center text-sm">
                  Nenhuma pergunta cadastrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <ModalDefault
      title={`Perguntas de exemplo${tool?.name ? ` - ${tool.name}` : ""}`}
      icon="article"
      show={show}
      onClose={onClose}
      sizeModal={"lg"}
      showFooter={true}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalChatToolQuestions"
        data-testid="ModalChatToolQuestions"
      >
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label>NOVA PERGUNTA</label>
              <InputGroup>
                <FormControl
                  placeholder="Ex.: inss - o que significa status PENDENTE no banco pan?"
                  value={questionInput}
                  disabled={isSaving}
                  onChange={(e) => setQuestionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                />
                <Button
                  variant="primary mb-0"
                  disabled={isSaving}
                  onClick={handleAdd}
                  title="Adicionar"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </InputGroup>
              <small className="text-muted">
                Servem apenas como referência/documentação de quando a tool deve
                ser acionada (não afetam o atendimento).
              </small>
            </FormGroup>
          </Col>
        </Row>

        <Card className="mt-2">
          <Card.Header>
            <h5 className="mb-0">Perguntas cadastradas</h5>
          </Card.Header>
          <Card.Body>{renderTable()}</Card.Body>
        </Card>
      </div>
    </ModalDefault>
  );
};

export default ModalChatToolQuestions;
